import React, { useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { SimState } from '../hooks/useSimulation';
import { FLOCK_COLORS } from './Board.component';

type DataPoint = { t: number; cyan: number; orange: number; green: number; pred: number };

type Props = {
  stateRef: React.MutableRefObject<SimState | null>;
  isPaused: boolean;
};

const SPECIES_DEFS = [
  { key: 'cyan' as const, label: 'Cyan', color: FLOCK_COLORS[0] },
  { key: 'orange' as const, label: 'Orange', color: FLOCK_COLORS[1] },
  { key: 'green' as const, label: 'Green', color: FLOCK_COLORS[2] },
  { key: 'pred' as const, label: 'Predator', color: '#ff1744' },
];

const PopulationGraph = ({ stateRef, isPaused }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const historyRef = useRef<DataPoint[]>([]);
  const animRef = useRef<number>(0);
  const isPausedRef = useRef(isPaused);

  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  useEffect(() => {
    const id = setInterval(() => {
      if (isPausedRef.current) return;
      const boids = stateRef.current?.boids ?? [];
      historyRef.current.push({
        t: Date.now(),
        cyan: boids.filter(b => b.type === 'fish' && b.flock === 0).length,
        orange: boids.filter(b => b.type === 'fish' && b.flock === 1).length,
        green: boids.filter(b => b.type === 'fish' && b.flock === 2).length,
        pred: boids.filter(b => b.type === 'predator').length,
      });
      const cutoff = Date.now() - 20 * 60 * 1000;
      historyRef.current = historyRef.current.filter(d => d.t >= cutoff);
    }, 1000);
    return () => clearInterval(id);
  }, [stateRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    const resize = () => {
      const parent = canvas.parentElement!;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    };
    resize();
    const ro = new ResizeObserver(() => requestAnimationFrame(resize));
    ro.observe(canvas.parentElement!);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      const history = historyRef.current;

      ctx.fillStyle = '#0d1f35';
      ctx.fillRect(0, 0, w, h);

      const PAD_L = 36;
      const PAD_R = 10;
      const PAD_T = 8;
      const PAD_B = 18;
      const plotW = w - PAD_L - PAD_R;
      const plotH = h - PAD_T - PAD_B;

      if (history.length < 2) {
        ctx.fillStyle = '#1e3a5f88';
        ctx.font = '11px monospace';
        ctx.textAlign = 'center';
        ctx.fillText('Collecting data…', PAD_L + plotW / 2, h / 2 + 4);
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      const tMin = history[0].t;
      const tMax = history[history.length - 1].t;
      const tRange = Math.max(tMax - tMin, 1);
      const maxCount = Math.max(...history.flatMap(d => [d.cyan, d.orange, d.green, d.pred]), 10);

      // Y grid + labels
      const ySteps = 4;
      for (let i = 0; i <= ySteps; i++) {
        const y = PAD_T + plotH * (1 - i / ySteps);
        ctx.strokeStyle = '#1a2a3a';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(PAD_L + plotW, y); ctx.stroke();
        ctx.fillStyle = '#607d8b';
        ctx.font = '9px monospace';
        ctx.textAlign = 'right';
        ctx.fillText(String(Math.round(i / ySteps * maxCount)), PAD_L - 4, y + 3);
      }

      // X time labels
      const elapsedSec = (tMax - tMin) / 1000;
      const xTicks = Math.min(5, history.length);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#607d8b';
      ctx.font = '9px monospace';
      for (let i = 0; i <= xTicks; i++) {
        const x = PAD_L + (i / xTicks) * plotW;
        const sec = Math.round((i / xTicks) * elapsedSec);
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        ctx.fillText(m > 0 ? `${m}m${s ? `${s}s` : ''}` : `${sec}s`, x, h - PAD_B + 11);
      }

      // Species lines + current value dot
      for (const { key, color } of SPECIES_DEFS) {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.lineJoin = 'round';
        let first = true;
        for (const pt of history) {
          const x = PAD_L + ((pt.t - tMin) / tRange) * plotW;
          const y = PAD_T + plotH * (1 - pt[key] / maxCount);
          if (first) { ctx.moveTo(x, y); first = false; } else ctx.lineTo(x, y);
        }
        ctx.stroke();

        const last = history[history.length - 1];
        const lx = PAD_L + plotW;
        const ly = PAD_T + plotH * (1 - last[key] / maxCount);
        ctx.beginPath();
        ctx.arc(lx, ly, 3, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animRef.current); ro.disconnect(); };
  }, []);

  const handleExport = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'population-graph.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, []);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
      <Box sx={{
        width: 90, flexShrink: 0,
        px: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75,
        borderRight: '1px solid #1e3a5f',
      }}>
        <Typography sx={{ color: '#607d8b', fontSize: 8, fontFamily: 'monospace', letterSpacing: 0.5, mb: 0.25 }}>
          POPULATION
        </Typography>
        {SPECIES_DEFS.map(({ label, color }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 2, bgcolor: color, borderRadius: 1, flexShrink: 0 }} />
            <Typography sx={{ color: '#90a4ae', fontSize: 10, fontFamily: 'monospace' }}>{label}</Typography>
          </Box>
        ))}
        <Tooltip title="Export as PNG" placement="top">
          <IconButton
            onClick={handleExport}
            size="small"
            sx={{
              mt: 0.25, alignSelf: 'flex-start',
              color: '#607d8b', border: '1px solid #1e3a5f', borderRadius: 1, p: 0.25,
              '&:hover': { color: '#00e5ff', borderColor: '#00e5ff44' },
            }}
          >
            <DownloadIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <canvas ref={canvasRef} style={{ display: 'block' }} />
      </Box>
    </Box>
  );
};

export default PopulationGraph;
